import React from "react";
import { color, radius, space, text, weight } from "../tokens";

/**
 * <Alert> — inline banner with semantic tone.
 *
 *   tone         : "info" default | "success" | "warning" | "danger"
 *   title        : short bold headline
 *   description  : body copy (ReactNode)
 *   icon         : ReactNode override (auto per tone if not provided)
 *   onClose      : if provided, renders dismiss button
 *   actions      : optional right-side action slot
 *
 * Use for portfolio-level "something needs attention" messages.
 * Not for form validation — use inline field errors for that.
 */
const TONE_STYLES = {
  info:    { bg: color.info.bg,    border: color.info.border,    fg: color.info.text,    icon: "ⓘ"  },
  success: { bg: color.success.bg, border: color.success.border, fg: color.success.text, icon: "✓"  },
  warning: { bg: color.warning.bg, border: color.warning.border, fg: color.warning.text, icon: "!"  },
  danger:  { bg: color.danger.bg,  border: color.danger.border,  fg: color.danger.text,  icon: "⚠"  },
};

export function Alert({
  tone = "info",
  title,
  description,
  icon,
  onClose,
  actions,
  style,
}) {
  const s = TONE_STYLES[tone] || TONE_STYLES.info;
  return (
    <div
      role="alert"
      style={{
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: radius.lg,
        padding: space[4],
        display: "flex",
        gap: space[3],
        alignItems: "flex-start",
        ...style,
      }}
    >
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: radius.pill,
          background: s.fg,
          color: "#FFF",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: text.base,
          fontWeight: weight.semibold,
        }}
      >
        {icon ?? s.icon}
      </span>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: space[1] }}>
        {title && (
          <div style={{ fontSize: text.md, fontWeight: weight.semibold, color: s.fg, lineHeight: 1.3 }}>
            {title}
          </div>
        )}
        {description && (
          <div style={{ fontSize: text.base, color: color.text.secondary, lineHeight: 1.5 }}>
            {description}
          </div>
        )}
        {actions && <div style={{ marginTop: space[2], display: "flex", gap: space[2] }}>{actions}</div>}
      </div>
      {onClose && (
        <button
          onClick={onClose}
          aria-label="Dismiss"
          style={{
            flexShrink: 0,
            background: "transparent",
            border: "none",
            color: s.fg,
            opacity: 0.6,
            cursor: "pointer",
            padding: space[1],
            fontSize: text.md,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}
